import type {
  SmartFieldUiComponentDateTimeOptionsApi,
  SmartFieldUiComponentInputStringOptionsApi,
  TemplateResourceParameterFieldV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import type {
  FGInputCustomModel,
  FGInputDateFormat,
  FGInputDateMode,
  FGInputDateModel,
  FGInputModel,
  FGInputMultiSelectModel,
  FGInputNumberModel,
  FGInputSelectModel,
  FGInputsType,
  FGInputTableModel,
  FGInputTextMode,
  FGInputTextModel,
  FGInputTimeFormat,
  FGInputToggleModel,
  FormGeneratorTableValue,
} from '@ingenico/react-form-generator';
import { EMPTY_VALUE, FGInputTypeEnum } from '@ingenico/react-form-generator';
import { toArray } from 'lodash-es';
import type { TemplateDetailedResourceParameterField } from '../../models/templateResource.model';

function getDatetimeFieldMode(field?: SmartFieldUiComponentDateTimeOptionsApi): FGInputDateMode {
  switch (field?.type) {
    case 'DATE_TIME': {
      return 'datetime';
    }
    case 'TIME': {
      return 'time';
    }
    default: {
      return 'date';
    }
  }
}

function getDefaultValue<T extends FGInputCustomModel>(
  configModuleValue: T['inputDefaultValue'],
  inputDefaultValue?: T['inputDefaultValue'],
): T['inputDefaultValue'] {
  return configModuleValue === undefined ? inputDefaultValue : configModuleValue;
}

export function generateInputTextCustomConfig(
  { definition: { common }, id }: TemplateDetailedResourceParameterField,
  valuesMapping: Record<string, FGInputsType>,
): FGInputCustomModel {
  const [uiComponentType] = Object.keys(common?.uiComponentOptions ?? {});

  switch (uiComponentType) {
    case 'multiSelect': {
      const multiSelectFieldOption = common?.uiComponentOptions?.multiSelect;
      const defaultValue = multiSelectFieldOption?.defaultSelectedIds?.reduce((acc, selectedId) => {
        const selectedValue = multiSelectFieldOption?.choices.find((choice) => choice.id === selectedId);
        if (selectedValue) {
          acc.push(selectedValue.value);
        }
        return acc;
      }, [] as string[]);
      return {
        type: FGInputTypeEnum.MultiSelect,
        inputOptions: toArray(multiSelectFieldOption?.choices),
        rules: {
          maxLength: multiSelectFieldOption?.max,
          minLength: multiSelectFieldOption?.min,
          required: multiSelectFieldOption?.isRequired,
        },
        hint: multiSelectFieldOption?.hint,
        label: multiSelectFieldOption?.label,
        inputDefaultValue: getDefaultValue<FGInputMultiSelectModel>(valuesMapping[id] as string[], defaultValue),
        inheritedValue: EMPTY_VALUE,
        localizedValue: EMPTY_VALUE,
        currentLocalizedValue: EMPTY_VALUE,
        isManuallyLocalizedByUser: false,
        isManuallyRestoredToInheritedByUser: false,
      } satisfies FGInputMultiSelectModel;
    }
    case 'select': {
      const selectField = common?.uiComponentOptions?.select;
      const defaultValue = selectField?.choices.find((choice) => choice.id === selectField?.defaultSelectedId)?.value;
      return {
        type: FGInputTypeEnum.Select,
        inputOptions: toArray(selectField?.choices),
        hint: selectField?.hint,
        label: selectField?.label,
        rules: {
          required: selectField?.isRequired,
        },
        inputDefaultValue: getDefaultValue<FGInputSelectModel>(valuesMapping[id] as string, defaultValue),
        inheritedValue: EMPTY_VALUE,
        localizedValue: EMPTY_VALUE,
        currentLocalizedValue: EMPTY_VALUE,
        isManuallyLocalizedByUser: false,
        isManuallyRestoredToInheritedByUser: false,
      } satisfies FGInputSelectModel;
    }
    case 'dateTime': {
      const dateField = common?.uiComponentOptions?.dateTime;
      return {
        type: FGInputTypeEnum.Date,
        mode: getDatetimeFieldMode(dateField),
        rules: {
          required: dateField?.isRequired,
        },
        hint: dateField?.hint,
        endDate: dateField?.endDate,
        startDate: dateField?.startDate,
        label: dateField?.label,
        inputDefaultValue: valuesMapping[id] as string,
        inputFormat: dateField?.format as FGInputDateFormat | FGInputTimeFormat,
        inheritedValue: EMPTY_VALUE,
        localizedValue: EMPTY_VALUE,
        currentLocalizedValue: EMPTY_VALUE,
        isManuallyLocalizedByUser: false,
        isManuallyRestoredToInheritedByUser: false,
      } satisfies FGInputDateModel;
    }
    default: {
      const stringFieldOption = common?.uiComponentOptions?.inputString;
      return {
        type: FGInputTypeEnum.Text,
        inputType: getTextInputType(stringFieldOption),
        placeholder: stringFieldOption?.placeholder,
        hint: stringFieldOption?.hint,
        label: stringFieldOption?.label,
        rules: {
          required: stringFieldOption?.isRequired,
          pattern: stringFieldOption?.pattern?.value as unknown as RegExp,
        },
        inputDefaultValue: valuesMapping[id] as string,
        inheritedValue: EMPTY_VALUE,
        localizedValue: EMPTY_VALUE,
        currentLocalizedValue: EMPTY_VALUE,
        isManuallyLocalizedByUser: false,
        isManuallyRestoredToInheritedByUser: false,
      } satisfies FGInputTextModel;
    }
  }
}

function getTextInputType(field?: SmartFieldUiComponentInputStringOptionsApi): FGInputTextMode {
  switch (field?.type) {
    case 'SECRET': {
      return 'password';
    }
    default: {
      return 'text';
    }
  }
}

export function generateToggleInputCustomConfig(
  { definition: { common }, id }: TemplateDetailedResourceParameterField,
  valuesMapping: Record<string, FGInputsType>,
): FGInputToggleModel {
  const toggleField = common?.uiComponentOptions?.toggle;
  return {
    type: FGInputTypeEnum.Toggle,
    inputDefaultValue: valuesMapping[id] as boolean,
    rules: undefined,
    hint: toggleField?.hint,
    label: toggleField?.label,
    localizedValue: EMPTY_VALUE,
    inheritedValue: EMPTY_VALUE,
    currentLocalizedValue: EMPTY_VALUE,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
  };
}

export function generateNumberInputCustomConfig(
  { definition: { common }, id }: TemplateDetailedResourceParameterField,
  valuesMapping: Record<string, FGInputsType>,
): FGInputNumberModel {
  const numberField = common?.uiComponentOptions?.inputNumber;
  return {
    type: FGInputTypeEnum.Number,
    rules: {
      max: numberField?.max,
      min: numberField?.min,
      required: numberField?.isRequired,
    },
    hint: numberField?.hint,
    label: numberField?.label,
    placeholder: numberField?.placeholder,
    inputDefaultValue: valuesMapping[id] as number,
    inheritedValue: EMPTY_VALUE,
    localizedValue: EMPTY_VALUE,
    currentLocalizedValue: EMPTY_VALUE,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
  };
}

export function generateFGSchemaFromStructure(
  structureFields: TemplateResourceParameterFieldV2Api[],
  valuesMapping: Record<string, FGInputsType>,
  disabled?: boolean,
): FGInputTableModel<FGInputModel>['inputsSchema'] {
  return structureFields.map((field) => {
    const mappedField = {
      ...field,
      dataType: (field.dataType as string).toUpperCase() as TemplateDetailedResourceParameterField['dataType'],
    } as TemplateDetailedResourceParameterField;

    return {
      disabled: !!disabled,
      inputKey: field.id,
      inputLabel: field.name,
      description: field.description,
      inputCustomConfig: manageInputCustomConfig(mappedField, valuesMapping),
    };
  });
}

export function manageInputCustomConfig(
  field: TemplateDetailedResourceParameterField,
  valuesMapping: Record<string, FGInputsType>,
  disabled?: boolean,
): FGInputCustomModel {
  switch (String(field.dataType).toUpperCase()) {
    case 'BOOLEAN': {
      return generateToggleInputCustomConfig(field, valuesMapping);
    }
    case 'NUMBER': {
      return generateNumberInputCustomConfig(field, valuesMapping);
    }
    case 'STRUCTURE': {
      return generateStructureInputCustomConfig(field, valuesMapping, disabled);
    }
    case 'DATA_FILE': {
      return generateInputTextCustomConfig(field, valuesMapping);
    }
    default: {
      return generateInputTextCustomConfig(field, valuesMapping);
    }
  }
}

export function generateStructureInputCustomConfig(
  { definition: { structure }, id }: TemplateDetailedResourceParameterField,
  valuesMapping: Record<string, FGInputsType>,
  disabled?: boolean,
): FGInputCustomModel {
  return {
    type: FGInputTypeEnum.Table,
    rules: undefined,
    inputsSchema: generateFGSchemaFromStructure(toArray(structure?.childFields), valuesMapping, disabled),
    inputDefaultValue: valuesMapping[id] as FormGeneratorTableValue,
    localizedValue: EMPTY_VALUE,
    inheritedValue: EMPTY_VALUE,
    currentLocalizedValue: EMPTY_VALUE,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
  } satisfies FGInputTableModel<FGInputModel>;
}
